import { ApiResponse } from './ApiResponse';
import { BaseApiService } from './BaseApiService';
import { InternshipService } from './internships/InternshipService';
import { apiUrls } from './ApiUrls';
import type { Internship } from './types/internship.types';
import type { UserEducation, UserSkill } from './types/userprofile.types';

export interface ValidationResult {
  status: 'loading' | 'success' | 'error' | 'warning' | 'pending';
  message: string;
  details?: any;
}

export interface UserProfileData {
  education: UserEducation[];
  skills: UserSkill[];
  personalInfo?: any;
  workExperience?: any;
}

export interface SupportingDocument {
  internshipDocumentId: number;
  documentTypeCode: string;
  documentTypeName: string;
  documentNameCode: string;
  documentNameName: string;
  isMandatory: boolean;
}

export interface ValidationResponse {
  eligibility: ValidationResult;
  skills: ValidationResult;
  experience?: ValidationResult;
  userProfile: UserProfileData | null;
  supportingDocuments?: SupportingDocument[];
  profileMatch?: string;
}

/**
 * Service class for application validation
 * Handles validation of user profile against internship requirements
 */
class ApplicationValidationService extends BaseApiService {
  private static instance: ApplicationValidationService;

  public static getInstance(): ApplicationValidationService {
    if (!ApplicationValidationService.instance) {
      ApplicationValidationService.instance = new ApplicationValidationService();
    }
    return ApplicationValidationService.instance;
  }

  /**
   * Get user profile data for validation
   */
  static async getUserProfileData(): Promise<UserProfileData> {
    try {
      console.log('👤 [ApplicationValidationService] Fetching user profile data...');
      
      // For now, return a basic structure since we're using backend validation
      // The actual profile data will come from the backend validation response
      const userProfile: UserProfileData = {
        education: [],
        skills: [],
        personalInfo: null
      };

      console.log('✅ [ApplicationValidationService] User profile data prepared:', {
        educationCount: userProfile.education.length,
        skillsCount: userProfile.skills.length,
        hasPersonalInfo: !!userProfile.personalInfo
      });

      return userProfile;
    } catch (error: any) {
      console.error('❌ [ApplicationValidationService] Error preparing user profile data:', error);
      throw new Error('Failed to prepare user profile data');
    }
  }

  /**
   * Validate user eligibility against internship requirements
   */
  static async validateEligibility(
    internship: Internship, 
    userProfile: UserProfileData
  ): Promise<ValidationResult> {
    try {
      console.log('🎯 [ApplicationValidationService] Validating eligibility...');
      
      if (!internship?.eligibility || !userProfile?.education || userProfile.education.length === 0) {
        return {
          status: 'error',
          message: 'Eligibility criteria or user education not found'
        };
      }

      const internshipEligibility = internship.eligibility[0]; // Assuming single eligibility criteria
      const userEducation = userProfile.education[0]; // Assuming primary education

      if (!internshipEligibility || !userEducation) {
        return {
          status: 'error',
          message: 'Eligibility criteria or user education not found'
        };
      }

      // Compare education criteria - using available properties
      const matches = [
        userEducation.course === internshipEligibility.course,
        userEducation.specialization === internshipEligibility.specialization,
        userEducation.grade === internshipEligibility.grade
      ];

      const matchCount = matches.filter(Boolean).length;
      const matchPercentage = (matchCount / matches.length) * 100;

      console.log('📊 [ApplicationValidationService] Eligibility match:', {
        matchPercentage,
        matchCount,
        totalCriteria: matches.length,
        matches
      });

      if (matchPercentage >= 80) {
        return {
          status: 'success',
          message: `Eligibility criteria matched (${matchPercentage.toFixed(0)}%)`,
          details: { matchPercentage, matches, criteria: internshipEligibility }
        };
      } else if (matchPercentage >= 60) {
        return {
          status: 'warning',
          message: `Partial eligibility match (${matchPercentage.toFixed(0)}%)`,
          details: { matchPercentage, matches, criteria: internshipEligibility }
        };
      } else {
        return {
          status: 'error',
          message: `Low eligibility match (${matchPercentage.toFixed(0)}%)`,
          details: { matchPercentage, matches, criteria: internshipEligibility }
        };
      }
    } catch (error: any) {
      console.error('❌ [ApplicationValidationService] Error validating eligibility:', error);
      
      return {
        status: 'error',
        message: 'Failed to validate eligibility'
      };
    }
  }

  /**
   * Validate user skills against internship requirements
   */
  static async validateSkills(
    internship: Internship, 
    userProfile: UserProfileData
  ): Promise<ValidationResult> {
    try {
      console.log('🔧 [ApplicationValidationService] Validating skills...');
      
      if (!internship?.requiredSkills || !userProfile?.skills || userProfile.skills.length === 0) {
        return {
          status: 'error',
          message: 'Skills information not found'
        };
      }

      const requiredSkills = internship.requiredSkills.map(skill => skill.skillName);
      const userSkills = userProfile.skills.map(skill => skill.skillName);
      
      // Find matched skills (case-insensitive partial matching)
      const matchedSkills = requiredSkills.filter(requiredSkill => 
        userSkills.some(userSkill => 
          userSkill.toLowerCase().includes(requiredSkill.toLowerCase()) ||
          requiredSkill.toLowerCase().includes(userSkill.toLowerCase())
        )
      );

      const matchPercentage = (matchedSkills.length / requiredSkills.length) * 100;
      const missingSkills = requiredSkills.filter(skill => !matchedSkills.includes(skill));

      console.log('📊 [ApplicationValidationService] Skills match:', {
        matchPercentage,
        matchedSkills,
        missingSkills,
        totalRequired: requiredSkills.length
      });

      if (matchPercentage >= 70) {
        return {
          status: 'success',
          message: `Skills requirement met (${matchPercentage.toFixed(0)}%)`,
          details: { matchedSkills, requiredSkills, missingSkills, matchPercentage }
        };
      } else if (matchPercentage >= 40) {
        return {
          status: 'warning',
          message: `Partial skills match (${matchPercentage.toFixed(0)}%)`,
          details: { matchedSkills, requiredSkills, missingSkills, matchPercentage }
        };
      } else {
        return {
          status: 'error',
          message: `Low skills match (${matchPercentage.toFixed(0)}%)`,
          details: { matchedSkills, requiredSkills, missingSkills, matchPercentage }
        };
      }
    } catch (error: any) {
      console.error('❌ [ApplicationValidationService] Error validating skills:', error);
      return {
        status: 'error',
        message: 'Failed to validate skills'
      };
    }
  }

  /**
   * Run validation for a job application using backend endpoint (same shape as internship validate).
   */
  static async runJobValidation(jobId: number | string): Promise<ValidationResponse> {
    try {
      const response = await this.getAuthenticated(
        apiUrls.jobs.applyValidate(jobId),
        'validate profile for job'
      );

      if (response.status === 'success' && response.data) {
        return ApplicationValidationService.transformBackendValidation(response.data);
      }

      let errorMessage = 'Backend validation failed';
      if (response.message) {
        errorMessage = response.message;
      } else if (response.error) {
        errorMessage = response.error;
      }
      throw new Error(errorMessage);
    } catch (error: any) {
      if (error.message?.includes('Request failed') || error.message?.includes('Authenticated request failed')) {
        let detailedErrorMessage = error.message;
        if (error.response?.data?.message) {
          detailedErrorMessage = error.response.data.message;
        } else if (error.data?.message) {
          detailedErrorMessage = error.data.message;
        }
        throw new Error(detailedErrorMessage);
      }
      throw error;
    }
  }

  private static normalizeMissingSkill(skill: any) {
    const yearsRaw =
      skill?.yearsOfExperience ?? skill?.years_of_experience ?? skill?.experienceYears ?? skill?.experience_years;
    const years =
      yearsRaw == null || Number.isNaN(Number(yearsRaw)) ? undefined : Math.round(Number(yearsRaw));
    return {
      skillName: skill?.skillName ?? skill?.skill_name,
      skillCode: skill?.skillCode ?? skill?.skill_code,
      proficiencyLevelName: skill?.proficiencyLevelName ?? skill?.proficiency_level_name,
      industryTypeName: skill?.industryTypeName ?? skill?.industry_type_name,
      yearsOfExperience: years,
      isMandatory: skill?.isMandatory ?? skill?.is_mandatory,
    };
  }

  private static transformBackendValidation(backendValidation: any): ValidationResponse {
    const eligibilityResult: ValidationResult = {
      status: backendValidation.eligibility?.match === 'success' ? 'success' : 'error',
      message: backendValidation.eligibility?.message || 'Eligibility validation failed',
      details: {
        matchPercentage: backendValidation.eligibility?.match === 'success' ? 100 : 0,
        matches: backendValidation.eligibility?.match === 'success' ? [true, true, true] : [false, false, false],
        missingCourses: backendValidation.eligibility?.missingCourses || [],
      },
    };

    const skillsResult: ValidationResult = {
      status: backendValidation.requiredSkills?.match === 'success' ? 'success' : 'error',
      message: backendValidation.requiredSkills?.message || 'Skills validation failed',
      details: {
        matchPercentage: backendValidation.requiredSkills?.match === 'success' ? 100 : 0,
        missingSkills: (backendValidation.requiredSkills?.missingSkills || []).map(
          ApplicationValidationService.normalizeMissingSkill
        ),
      },
    };

    const experiencePayload = backendValidation.experience;
    const experienceResult: ValidationResult | undefined = experiencePayload
      ? {
          status: experiencePayload.match === 'success' ? 'success' : 'error',
          message: experiencePayload.message || 'Experience validation failed',
          details: {
            requiredExperienceFromYears:
              experiencePayload.requiredExperienceFromYears ??
              experiencePayload.required_experience_from_years,
            requiredExperienceToYears:
              experiencePayload.requiredExperienceToYears ??
              experiencePayload.required_experience_to_years,
            studentExperienceYears:
              experiencePayload.studentExperienceYears ??
              experiencePayload.student_experience_years,
          },
        }
      : undefined;

    return {
      eligibility: eligibilityResult,
      skills: skillsResult,
      experience: experienceResult,
      userProfile: null,
      supportingDocuments: backendValidation.supportingDocuments || [],
      profileMatch: backendValidation.profileMatch,
    };
  }

  /**
   * Run complete validation for an internship application using backend endpoint
   */
  static async runValidation(internship: Internship): Promise<ValidationResponse> {
    try {
      console.log('🚀 [ApplicationValidationService] Starting validation for internship:', internship.internshipCode || internship.id);
      
      // Call the backend validation endpoint
      const validationUrl = internship.internshipCode
        ? apiUrls.internships.applyValidateByCode(internship.internshipCode)
        : apiUrls.internships.applyValidate(internship.id);
      const response = await this.getAuthenticated(validationUrl, 'validate profile for internship');
      
      if (response.status === 'success' && response.data) {
        const backendValidation = response.data;
        
        console.log('📊 [ApplicationValidationService] Backend validation response:', backendValidation);
        
        const validationResponse = ApplicationValidationService.transformBackendValidation(backendValidation);

        console.log('✅ [ApplicationValidationService] Backend validation completed:', {
          profileMatch: backendValidation.profileMatch,
          eligibilityStatus: validationResponse.eligibility.status,
          skillsStatus: validationResponse.skills.status,
          documentsCount: backendValidation.supportingDocuments?.length || 0
        });

        return validationResponse;
      } else {
        // Extract error message from the backend response
        let errorMessage = 'Backend validation failed';
        
        if (response.message) {
          errorMessage = response.message;
        } else if (response.error) {
          errorMessage = response.error;
        }
        
        console.error('❌ [ApplicationValidationService] Backend validation failed:', response);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ [ApplicationValidationService] Error running validation:', error);
      
      // If this is an error thrown by BaseApiService, try to extract the original error message
      if (error.message && (error.message.includes('Request failed') || error.message.includes('Authenticated request failed'))) {
        // This is likely a BaseApiService error, try to get more details
        console.log('📝 [ApplicationValidationService] BaseApiService error detected, attempting to extract details');
        
        // Try to extract error from the error object itself
        let detailedErrorMessage = error.message;
        
        // Check if there's additional error information in the error object
        if (error.response?.data?.message) {
          detailedErrorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          detailedErrorMessage = error.response.data.error;
        } else if (error.data?.message) {
          detailedErrorMessage = error.data.message;
        } else if (error.data?.error) {
          detailedErrorMessage = error.data.error;
        }
        
        throw new Error(detailedErrorMessage);
      }
      
      throw error; // Re-throw to let the component handle the error
    }
  }

  /**
   * Submit application for an internship
   */
  static async submitApplication(
    internshipId: number,
    uploadedDocuments?: string[],
    applicationNotes?: string,
    internshipCode?: string
  ): Promise<ApiResponse<any>> {
    try {
      console.log('📝 [ApplicationValidationService] Submitting application for internship:', internshipCode || internshipId);
      
      // Use the existing InternshipService to submit the application
      // The service will format the payload correctly for the backend
      const response = internshipCode
        ? await InternshipService.applyForInternshipByCode(internshipCode, uploadedDocuments || [], applicationNotes)
        : await InternshipService.applyForInternship(internshipId, uploadedDocuments || [], applicationNotes);
      
      console.log('✅ [ApplicationValidationService] Application submitted successfully');
      return response;
    } catch (error: any) {
      console.error('❌ [ApplicationValidationService] Error submitting application:', error);
      
      // Extract error message from various error response formats
      let errorMessage = 'Failed to submit application';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      }
      
      console.log('📝 [ApplicationValidationService] Extracted error message:', errorMessage);
      
      return {
        status: 'error',
        message: errorMessage
      };
    }
  }
}

// Export the class for static method access
export { ApplicationValidationService }; 